import { Order } from "@/types/ApiTypes";
import { handlePrintForAllPlatforms } from "@/utils/PrintForAllPlatforms";
import { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import PrintOrderContent from "./PrintOrderContent";

interface Props {
    order: Order;
    isDragging?: boolean;
    title?: string;
}

export default function OrderCard({ order, isDragging = false, title }: Props) {
    const { t, i18n } = useTranslation();
    const locale = (i18n.language || "en").split("-")[0];
    const contentRef = useRef<HTMLDivElement>(null);
    const toastIdRef = useRef<string>();
    const [printOrder, setPrintOrder] = useState<Order | null>(null);
    const [printLoading, setPrintLoading] = useState(false);
    const [arabicTime, setArabicTime] = useState("");
    const [englishTime, setEnglishTime] = useState("");
    const printTimeoutRef = useRef<NodeJS.Timeout>();

    const reactToPrintFn = useReactToPrint({
        contentRef,

    });

    useEffect(() => {
        if (printLoading) {
            toastIdRef.current = toast.loading(t("ordersBody.Loading") as string);
        } else if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toastIdRef.current = undefined;
        }
    }, [printLoading, t]);

    useEffect(() => {
        if (order?.time) {
            formatTimestamp(order.time);
        }
    }, [order?.time]);

    const handlePrint = async () => {
        if (printLoading) return;
        await handlePrintOrder(order);
    };

    const handlePrintOrder = async (orderToPrint: Order) => {
        setPrintLoading(true);
        setPrintOrder({ ...orderToPrint });

        // Safety timeout (cleanup if print fails)
        printTimeoutRef.current = setTimeout(() => {
            setPrintLoading(false);
            setPrintOrder(null);
        }, 5000); // 5 second fallback

        try {
            await new Promise(resolve => setTimeout(resolve, 50)); // DOM update delay

            // Print and wait for it to complete
            const printPromise = handlePrintForAllPlatforms(reactToPrintFn, contentRef);

            // Race between printing and timeout
            await Promise.race([
                printPromise,
                new Promise(resolve => setTimeout(resolve, 3000)) // Max 3s for print
            ]);

            // Keep content for at least 2s after printing starts
            await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
            console.error("Print error:", error);
            toast.error(t("ordersBody.PrintError") as string);
        } finally {
            clearTimeout(printTimeoutRef.current);
            setPrintLoading(false);
            setPrintOrder(null);
        }
    };

    function formatTimestamp(timestamp: number) {
        const date = new Date(timestamp * 1000);
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        };

        const formattedEnglish = date.toLocaleString("en-US", options);
        const formattedArabicRaw = date.toLocaleString("ar-EG", options);

        const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
        const englishNumbers = "0123456789";

        const formattedArabic = formattedArabicRaw.replace(/[٠-٩]/g, (d) =>
            englishNumbers[arabicNumbers.indexOf(d)]
        );

        setEnglishTime(formattedEnglish);
        setArabicTime(formattedArabic);
    }

    return (
        <>
            {printOrder && (

                <PrintOrderContent
                    printTarget={printOrder}
                    contentRef={contentRef}
                    arabicTime={arabicTime}
                    englishTime={englishTime}
                />

            )}

            <div
                className={`max-w-sm mx-auto py-5 px-1 my-1 rounded-xl shadow-md flex flex-col items-center space-y-2 cursor-grab ${isDragging
                    ? "bg-sidebar text-skyblue shadow-xl"
                    : title === "Finished Orders"
                        ? "bg-green-200"
                        : title === "Preparing Orders"
                            ? "bg-blue-200"
                            : "bg-yellow-100"
                    }`}
            >
                <div className="flex items-center space-x-2">
                    <h2 className="text-lg font-semibold">Order # {order.orderId}</h2>
                </div>

                <div className="flex items-center space-x-2 text-sm">
                    <span>
                        {order?.items?.map((item, index) => {
                            const isLastItem =
                                index === (order?.invoice?.items?.length ?? 0) - 1;
                            return (
                                <div key={index} className="flex flex-col items-center gap-1">
                                    <div className="flex items-center gap-10 justify-between">
                                        <span
                                            className="whitespace-normal flex flex-row-reverse gap-5"
                                            dir="rtl"
                                        >
                                            <span dir="ltr"> {item.quantity} X </span>
                                            <span>
                                                <span className="flex justify-center">
                                                    {item.nameEN}
                                                </span>
                                                <span className="flex justify-center">
                                                    {item.nameAR !== item.nameEN ? item.nameAR : ""}
                                                </span>
                                            </span>
                                        </span>
                                    </div>

                                    {item.addons?.length > 0 && (
                                        <span className="flex flex-col items-center font-bold">
                                            {t("ordersBody.Addons")}
                                            <span className="font-normal flex items-center flex-col">
                                                {item.addons.map((addon, i) => (
                                                    <span key={i}>
                                                        {locale === "en" ? addon.nameEN : addon.nameAR}
                                                    </span>
                                                ))}
                                            </span>
                                        </span>
                                    )}

                                    {item.options?.length > 0 && (
                                        <span className="flex flex-col items-center font-bold">
                                            {t("ordersBody.Options")}
                                            <span className="flex flex-col font-normal items-center justify-center">
                                                {item.options.flatMap((option) =>
                                                    option.options.map((optItem, i) => (
                                                        <span key={i}>
                                                            {locale === "en"
                                                                ? optItem.nameEN
                                                                : optItem.nameAR}
                                                        </span>
                                                    ))
                                                )}
                                            </span>
                                        </span>
                                    )}
                                    {item.notes && <>
                                        <div className="flex flex-col items-center ">
                                            <span className="font-semibold">{t("ordersBody.ItemNote")}</span>
                                            <span>{item.notes}</span>
                                        </div>
                                    </>}
                                    {!isLastItem && (
                                        <div
                                            className={`w-full border-b-2 ${isDragging ? "border-skyblue" : "border-black"
                                                }`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </span>
                </div>

                <div className="flex items-center text-sm space-y-1 flex-col">
                    <div>
                        <span className="font-semibold">{t("orders.orderType")}:</span>{" "}
                        <span>
                            {order.deliveryType === "receiving"
                                ? t("ordersBody.Receiving")
                                : order.deliveryType === "dine-in"
                                    ? t("ordersBody.DineIn")
                                    : order.deliveryType === "cashier" &&
                                    t("cashierHistoryBody.cashier")}
                        </span>
                    </div>

                    {order.dineIn?.floor && order.dineIn?.table && (
                        <>
                            <div>
                                <span className="font-semibold">{t("ordersBody.Floor")}:</span>{" "}
                                <span>{order.dineIn.floor.number}</span>
                            </div>
                            <div>
                                <span className="font-semibold">{t("ordersBody.Table")}:</span>{" "}
                                <span>{order.dineIn.table.number}</span>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-center text-sm gap-1">
                    <span className="font-semibold">{t("orders.receiptTime")}:</span>
                    {order.deliveryTiming === "afap"
                        ? t("ordersBody.AsSoonAsPossible")
                        : order.deliveryTiming === "scheduled"
                            ? t("ordersBody.Scheduled")
                            : t("ordersBody.AsSoonAsPossible")}
                    {order.deliveryAt && (
                        <span className="flex gap-1 items-center">
                            <span>{t("ordersBody.ReceiveAt")}</span>
                            <span className="pt-1">{order.deliveryAt}</span>
                        </span>
                    )}
                </div>

                <div className="flex items-center justify-center text-sm gap-1">
                    <span className="font-semibold">
                        {order.type === "delivery" && t("chef.delivery")}
                        {order.type === "pickup" && t("chef.pickup")}
                        {order.type === "internal" && t("chef.internal")}
                    </span>
                </div>

                <div className="flex items-center text-sm space-x-2">
                    <span className="font-semibold">{t("orders.orderedTime")}:</span>
                    <span>{locale === "en" ? englishTime : arabicTime}</span>
                </div>

                <button
                    onClick={handlePrint}
                    type="button"
                    disabled={printLoading}
                    className={`my-btn w-[80%] ${isDragging ? "bg-skyblue text-sidebar hover:bg-skyblue hover:text-sidebar" : ""
                        }`}
                >
                    {printLoading ? t("ordersBody.Printing") : t("ordersBody.print")}
                </button>
            </div>
        </>
    );
}